import ContentHeader from 'components/UI/ContentHeader/ContentHeader';
import { MASTER_LINKS } from 'constants/master-list-tabs';
import AdvisoryService from 'services/AdvisoryService';
import { ReactQueryKeys } from 'constants/react-query-keys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { SweetAlertResult } from 'sweetalert2';
import SwalAlert, {
  firePreConfirmAlert,
} from 'components/UI/SwalAlert/SwalAlert';
import { toast } from 'react-hot-toast';
import { ADMINISTRATION, AUTHOR } from 'constants/roles';
import { useSelector } from 'react-redux';
import intersection from 'lodash.intersection';
import { useTranslation } from 'react-i18next';
import AdvisoryList from './AdvisoryList/AdvisoryList';
import { Advisory } from 'interfaces/advisory';
import Spinner from 'components/UI/Spinner/Spinner';
import HospitalTotalLabels from 'components/UI/HospitalTotalLabels/HospitalTotalLabels';
import { selectUser } from 'store/slices/auth';

const Advisories = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const user = useSelector(selectUser);
  const canManage = !!intersection([user?.roles], [ADMINISTRATION, AUTHOR])
    .length;

  const {
    data: advisories,
    isLoading: isLoadingAdvisories,
    isFetching,
  } = useQuery(
    [
      ReactQueryKeys.ADVISORIES,
      {
        columnFilters,
        pagination,
      },
    ],
    () =>
      AdvisoryService.get({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting: [
          {
            id: 'name',
            desc: false,
          },
        ],
        columnFilters: columnFilters,
      }),
    { keepPreviousData: true },
  );

  /**
   * Update search filters for the api call
   *
   * @param value The search value
   */
  const handleUpdateFilter = (value: string) => {
    setColumnFilters([
      {
        id: 'name',
        value,
      },
    ]);
  };

  // Show alert for confirming of deletion a record
  const handleDeleteShow = (advisory: Advisory) => {
    firePreConfirmAlert({
      title: t('advisories.messages.modal__delete__title'),
      html: t('advisories.messages.modal__delete__content', {
        name: advisory.name,
      }),
      preConfirm: () => {
        return AdvisoryService.delete(String(advisory.id))
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
            //return false to prevent pop up from closing when running tests, check preconfirm fn
            return false;
          });
      },
    }).then((data: SweetAlertResult) => {
      if (data.isConfirmed) {
        queryClient.invalidateQueries([ReactQueryKeys.ADVISORIES]);
        navigate('/advisories');
        toast.success(t('advisories.messages.delete__success'));
      }
    });
  };

  return (
    <>
      <ContentHeader links={MASTER_LINKS} />
      <div className="main-container with-tabs" data-testid="main-container">
        <div className="row">
          <div className="col-3">
            {isLoadingAdvisories ? (
              <Spinner isAbsolute />
            ) : (
              <>
                <HospitalTotalLabels
                  translationKey="advisories"
                  total={
                    advisories?.data.total ? advisories?.data.total - 1 : 0
                  }
                />
                <AdvisoryList
                  refetch={isLoadingAdvisories || isFetching}
                  canManage={canManage}
                  onDelete={handleDeleteShow}
                  updateFilter={handleUpdateFilter}
                  advisoriesResponse={advisories?.data}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              </>
            )}
          </div>
          <div className="col-9 ps-4">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
};

export default Advisories;
